import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { SafeDictionary } from "@insite/client-framework/Common/Types";

interface OwnProps extends WidgetProps {
    location?: {
        search: string;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    Partial<HasProductContext>;

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps & Partial<HasProductContext>) => {
    // 1. Try getting product from productContext if available
    let product = ownProps.productContext?.product;

    // 2. Extract location search string via getLocation selector or window fallback
    const location = getLocation(state);
    const locationSearch = location?.search || (typeof window !== "undefined" ? window.location.search : "") || ownProps.location?.search || "";
    
    // Type assertion fixes 'parsedQuery is of type unknown'
    const parsedQuery = parseQueryString(locationSearch) as SafeDictionary<string>;
    const productId = parsedQuery.productId || "";

    if (!product && productId) {
        product = getProductState(state, productId).value;
    }

    return {
        productId,
        product,
    };
};

const mapDispatchToProps = {
    loadProduct,
};

export interface XcProductInformationStyles {
    container?: GridContainerProps;
    imageGridItem?: GridItemProps;
    primaryImageStyles?: ProductPrimaryImageStyles;
    detailsGridItem?: GridItemProps;
    titleText?: TypographyProps;
    partNumbersWrapper?: InjectableCss;
    partNumberLabel?: TypographyProps;
    partNumberValue?: TypographyProps;
}

export const xcProductInformationStyles: XcProductInformationStyles = {
    container: {
        gap: 20,
    },
    imageGridItem: {
        width: [12, 12, 4, 4, 4],
    },
    primaryImageStyles: {
        img: {
            css: css`
                max-width: 100%;
                height: auto;
            `,
        },
    },
    detailsGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            margin-bottom: 8px;
        `,
    },
    partNumbersWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    partNumberLabel: {
        size: 14,
        weight: "bold",
        css: css`
            margin-right: 6px;
        `,
    },
    partNumberValue: {
        size: 14,
        css: css`
            margin-bottom: 5px;
            margin-right: 15px;
        `,
    },
};

const styles = xcProductInformationStyles;

const XcProductInformation: React.FC<Props> = ({ productId, product, loadProduct }) => {
    // If product isn't loaded into Redux store yet, dispatch loadProduct
    useEffect(() => {
    if (productId && !product) {
        loadProduct({ id: productId });
    }
}, [productId, product, loadProduct]);

    if (!product) {
        return null;
    }

    const image: ImageModel = {
        id: product.id,
        name: product.imageAltText || "",
        sortOrder: 0,
        imageAltText: product.imageAltText,
        imageType: "Static",
        largeImagePath: product.largeImagePath,
        mediumImagePath: product.mediumImagePath,
        smallImagePath: product.smallImagePath,
    };

    const modelNumber = product.manufacturerItem || product.detail?.modelNumber;
    const itemNumber = product.productNumber;

    return (
        <GridContainer {...styles.container}>
            {/* Left Column: Image */}
            <GridItem {...styles.imageGridItem}>
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    extendedStyles={styles.primaryImageStyles}
                />
            </GridItem>

            {/* Right Column: Title & Numbers */}
            <GridItem {...styles.detailsGridItem}>
                {product.productTitle && (
                    <Typography {...styles.titleText}>{product.productTitle}</Typography>
                )}

                <StyledWrapper {...styles.partNumbersWrapper}>
                    {modelNumber && (
                        <Typography {...styles.partNumberValue} as="p">
                            <Typography {...styles.partNumberLabel}>{translate("Model:")}</Typography>
                            {modelNumber}
                        </Typography>
                    )}
                    {itemNumber && (
                        <Typography {...styles.partNumberValue} as="p">
                            <Typography {...styles.partNumberLabel}>{translate("Item:")}</Typography>
                            {itemNumber}
                        </Typography>
                    )}
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(XcProductInformation),
    definition: {
        displayName: "Xc Product Information",
        group: "Product Details",
    },
};

export default widgetModule;